// pages/brand/CityBrand.jsx
import { useState, useEffect, useMemo, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useSEO } from "../../hooks/useSEO";
import {
  fetchModelProducts,
  clearModelData,
  setSelectedSeriesId,
  clearSelectedSeriesId,
} from "../../store/slices/modelSlice";
import { findCityBySlug } from "../../store/slices/citySlice";
import { useTheme } from "../../theme/ThemeProvider";
import Layout from "../../components/Layout";
import ModelGrid from "../../components/model/ModelGrid";
import ModelSkeleton from "../../components/model/ModelSkeleton";
import CategoryHeader from "../../components/category/CategoryHeader";
import ErrorState from "../../components/state/ErrorState";
import EmptyState from "../../components/state/EmptyState";
import SeriesFilter from "../../components/model/SeriesFilter";

const CityBrand = () => {
  const dispatch = useDispatch();
  const { citySlug, categorySlug, brandSlug } = useParams();
  const { isDarkMode } = useTheme();

  // Get city from Redux
  const { selectedCity, findingCity, findCityError } = useSelector(
    (state) => state.city,
  );
  const cityName = selectedCity?.name || "";

  const {
    loading,
    error,
    category: categoryData,
    brand,
    series,
    products,
    selectedSeriesId: selectedId,
  } = useSelector((state) => state.model);

  const [selectedSeriesId, setSelectedSeriesIdState] = useState("");

  const seriesList = useMemo(() => series || [], [series]);
  const productList = useMemo(() => products || [], [products]);
  const categoryName = categoryData?.name || "";
  const brandName = brand?.name || "";

  useEffect(() => {
    if (selectedId) setSelectedSeriesIdState(selectedId);
  }, [selectedId]);

  // ============= Handle City Selection =============
  useEffect(() => {
    if (citySlug) {
      const currentCitySlug = selectedCity?.slug;
      if (currentCitySlug !== citySlug) {
        dispatch(findCityBySlug(citySlug));
      }
    }
  }, [citySlug, selectedCity?.slug, dispatch]);

  // ============= SEO =============
  const seoParams = useMemo(() => {
    if (loading || !brand || !selectedCity) return null;

    return {
      categorySlug: categorySlug,
      brandSlug: brandSlug,
      categoryName: categoryData?.name || "",
      brandName: brand?.name || "",
      brandDescription: brand?.description || "",
      productCount: productList?.length || 0,
      cityName: selectedCity?.name || "",
      stateName: selectedCity?.state?.name || "",
      citySlug: selectedCity?.slug || "",
    };
  }, [
    loading,
    brand,
    categorySlug,
    brandSlug,
    categoryData,
    productList,
    selectedCity,
  ]);

  useSEO(
    selectedCity?.slug
      ? `/sell-used-online/in/${selectedCity.slug}/${categorySlug}/${brandSlug}`
      : null,
    seoParams,
  );

  // Fetch when category, brand, OR city changes
  useEffect(() => {
    if (
      categorySlug &&
      brandSlug &&
      selectedCity?.slug === citySlug &&
      !findingCity &&
      !findCityError
    ) {
      dispatch(clearModelData());
      dispatch(
        fetchModelProducts({
          category: categorySlug,
          model: brandSlug,
          selectedSeriesId,
        }),
      );
    }
    return () => {
      dispatch(clearModelData());
      dispatch(clearSelectedSeriesId());
    };
  }, [
    dispatch,
    categorySlug,
    brandSlug,
    selectedSeriesId,
    selectedCity?.slug,
    citySlug,
    findingCity,
    findCityError,
  ]);

  const handleSeriesChange = useCallback(
    (seriesId) => {
      setSelectedSeriesIdState(seriesId);
      dispatch(setSelectedSeriesId(seriesId));
    },
    [dispatch],
  );

  const filteredProducts = useMemo(() => {
    if (!selectedSeriesId) return productList;
    return productList.filter(
      (product) => product.series_id === parseInt(selectedSeriesId, 10),
    );
  }, [productList, selectedSeriesId]);

  const handleRetry = useCallback(() => {
    dispatch(
      fetchModelProducts({
        category: categorySlug,
        model: brandSlug,
        selectedSeriesId,
      }),
    );
    if (citySlug && selectedCity?.slug !== citySlug) {
      dispatch(findCityBySlug(citySlug));
    }
  }, [
    dispatch,
    categorySlug,
    brandSlug,
    selectedSeriesId,
    citySlug,
    selectedCity?.slug,
  ]);

  // Show loading while city is being found or doesn't match
  if (
    loading ||
    findingCity ||
    !selectedCity ||
    selectedCity?.slug !== citySlug
  ) {
    return (
      <Layout>
        <div className="container px-4 py-6 mx-auto max-w-7xl">
          <ModelSkeleton isDarkMode={isDarkMode} />
        </div>
      </Layout>
    );
  }

  if (error || findCityError) {
    return (
      <Layout>
        <div className="container px-4 py-6 mx-auto max-w-7xl">
          <ErrorState
            error={error || findCityError}
            onRetry={handleRetry}
            isDarkMode={isDarkMode}
          />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container px-4 py-6 mx-auto max-w-7xl">
        <CategoryHeader
          title={`Sell Your Old ${brandName} ${categoryName} in ${selectedCity?.name}`}
          subtitle={`Select your ${brandName} model to get the best price in ${selectedCity?.name}${
            selectedCity?.state?.name ? `, ${selectedCity.state.name}` : ""
          }`}
          isDarkMode={isDarkMode}
          image={categoryData?.image}
        />

        <SeriesFilter
          seriesList={seriesList}
          selectedSeriesId={selectedSeriesId}
          onSeriesChange={handleSeriesChange}
          isDarkMode={isDarkMode}
          productCount={filteredProducts.length}
        />

        {filteredProducts.length === 0 ? (
          <EmptyState
            isDarkMode={isDarkMode}
            title="No products available"
            message={`We couldn't find any products for ${brandName}${selectedSeriesId ? ` in this series` : ""} in ${selectedCity?.name}`}
          />
        ) : (
          <ModelGrid
            brand={brand}
            products={filteredProducts}
            category={categoryData}
            city={selectedCity}
            isDarkMode={isDarkMode}
          />
        )}
      </div>
    </Layout>
  );
};

export default CityBrand;
